import { D, type UnmatchedRow } from '@wave/shared';

/** Точка истории в терминах математики тренда (поля из HistoryPoint). */
export interface TrendPoint {
  ts: string;
  netUsdt: string;
  walletsUsdt: string;
}

/**
 * Медиана скользящего окна 3 — гасит одиночные выбросы (рассинхрон ног
 * операции почти всегда одиночный), но сохраняет реальные ступени, в отличие
 * от среднего. Края усечены: первая/последняя точка возвращают сами себя.
 */
export function medianSmooth(values: number[]): number[] {
  if (values.length < 3) return [...values];
  return values.map((v, i) => {
    if (i === 0 || i === values.length - 1) return v;
    const w = [values[i - 1]!, v, values[i + 1]!].sort((a, b) => a - b);
    return w[1]!;
  });
}

/**
 * Расхождение позиции: односторонний скачок (одна нога проводки легла,
 * вторая — в пути), чья компенсация проверяется в течение следующих 3 точек.
 *
 * Инвариант: `delta === walletsDelta − chatsDelta` (с точностью до плавающей
 * арифметики) — потому что позиция = кошельки − чистый остаток чатов. Все три
 * поля — ОДНОШАГОВЫЙ перепад между точками `index − 1` и `index` (см.
 * `detectDivergences`: начиная с этой версии функция не телескопирует
 * остатки в цепочку событий, поэтому накопленных величин здесь больше нет).
 */
export interface Divergence {
  index: number;
  ts: string;
  /** Скачок позиции между точками index−1 и index. */
  delta: number;
  /** Вклад чатов (изменение netUsdt между index−1 и index, знак как в данных). */
  chatsDelta: number;
  /** Вклад кошельков (изменение walletsUsdt между index−1 и index). */
  walletsDelta: number;
  /**
   * `closed` — компенсация найдена в пределах 3 следующих точек;
   * `open` — прошли все 3 точки, компенсации нет, и сверка с журналом
   * (`confirmDivergences`) нашла непарную строку, подтверждающую скачок;
   * `pending` — кандидат слишком свежий (до конца истории меньше 3 точек),
   * статус пока не определён — не путать с `open`;
   * `explained` — как и `open`, прошли все 3 точки без компенсации на
   * графике, но сверка с журналом НЕ нашла подходящей непарной строки —
   * скачок объяснён обычными операциями (все ноги нашли пару), тревога
   * ложная. Присваивается только `confirmDivergences`, не `detectDivergences`
   * (сам детектор такого статуса не знает — он смотрит только на график).
   */
  status: 'closed' | 'open' | 'pending' | 'explained';
  /** Время точки, где скачок компенсировался (только для `closed`). */
  closedAtTs: string | null;
  /**
   * Самая крупная непарная строка журнала в окне — подтверждает расхождение
   * (только для `open`, когда её масштаб сопоставим со скачком, см.
   * `confirmDivergences`). НЕ обязана совпадать с `delta` по величине:
   * скачок агрегирован по бакету и обычно складывается из нескольких
   * операций.
   */
  evidence?: { title: string; ccy: string; delta: string; deltaUsdt: string; actor: string | null };
  /**
   * `open` остался `open` не потому, что сверка нашла подтверждение, а
   * потому, что сама сверка была неполной (`UnmatchedResponse.truncated` —
   * сервер обрезал окно по `MAX_ROWS`, см. `matching.ts`): понижать до
   * `explained` в этом случае нельзя — настоящая пара могла попасть в
   * отброшенный (более старый) хвост. Взаимоисключим с `evidence`: если
   * совпадение всё же нашлось несмотря на обрезку, ставим `evidence`, а не
   * этот флаг.
   */
  matchIncomplete?: boolean;
}

/** Позиция точки: кошельки − чистый остаток чатов (decimal, без parseFloat). */
function position(p: TrendPoint): number {
  return D(p.walletsUsdt).minus(D(p.netUsdt)).toNumber();
}

/**
 * Ищет односторонние скачки позиции (вероятный рассинхрон ног проводки:
 * одна нога уже легла, вторая — в пути или зависла) и проверяет, вернулась
 * ли позиция назад в пределах следующих 3 точек.
 *
 * Опция `bucketMinutes` — размер бакета, которым агрегированы точки истории
 * (см. `apps/api/.../chat-balances/history.ts`, RANGES). На бакетах ≥4 часов
 * (30д/90д/«Всё») естественный дрейф позиции между соседними точками почти
 * всегда превышает порог сам по себе — детектор рассинхрона ног там
 * бессмысленен (он ловит десятки минут задержки между ногами, а не дни),
 * поэтому на таких бакетах функция сразу возвращает `[]`.
 *
 * Критерий одностороннести (главное отличие от предыдущей версии): скачок
 * позиции считается кандидатом ТОЛЬКО если одна из двух ног (чаты/кошельки)
 * почти не двигалась относительно другой —
 * `min(|chatsDelta|, |walletsDelta|) <= 0.15 × max(|chatsDelta|, |walletsDelta|)`.
 * Если обе ноги двигались навстречу друг другу сопоставимыми суммами — это
 * маржа завершившейся сделки (обе ноги уже легли, разница — заработок), а не
 * рассинхрон, и кандидатом она не становится, даже если сам скачок позиции
 * превышает порог.
 *
 * Закрытие ищем с lookahead до 3 точек: накопленная сумма скачков позиции
 * `delta[i] + delta[i+1] + ... + delta[i+k]` (k = 1..3) сравнивается с
 * исходным `delta[i]` — как только на каком-то шаге накопленная сумма попадёт
 * в пределы `0.1 × |delta[i]|`, расхождение закрывается в этой точке
 * (`status: 'closed'`, `closedAtTs` = ts точки закрытия). Точки внутри
 * интервала закрытия (index+1..index+k) НЕ проверяются как отдельные
 * кандидаты — иначе остаток закрытия (маржа сделки) снова становится
 * поводом для нового события, что и было первопричиной ложных алертов
 * («вторая нога не проведена» на каждой крупной сделке).
 *
 * Если после кандидата в истории осталось меньше 3 точек — статус
 * `'pending'` (недостаточно данных, а не подтверждённый «висяк»). `'open'`
 * ставится только когда все 3 точки lookahead проверены и компенсации не
 * нашлось.
 *
 * Известное ограничение: остаток от закрытия НЕ порождает новое событие,
 * даже если он сам является началом новой односторонней операции (пример:
 * крупная сделка закрылась почти без остатка, а сразу следом стартовала
 * новая, независимая, — на той же точке). Такая операция будет замечена
 * только со следующего цикла (следующей точки истории, где её собственный
 * скачок проверится с нуля). Это осознанный компромисс: цена ложноотрицательного
 * результата (на один цикл позже) сильно ниже цены ложноположительного
 * (красный алерт на каждой крупной сделке с маржой).
 *
 * Второе известное ограничение (следствие критерия одностороннести): если
 * вторая нога легла ЧАСТИЧНО в том же бакете — или по другой стороне прошла
 * параллельная активность — отношение ног выходит за 0.15, кандидат не
 * создаётся, и реальный рассинхрон пропускается. Зона тем шире, чем крупнее
 * бакет: chatsDelta/walletsDelta — агрегаты по ВСЕМ чатам и кошелькам, и на
 * часовом бакете встречное движение по обеим сторонам вероятнее, чем на
 * 15-минутном. Размен осознанный: пропущенный алерт дешевле алерта, которому
 * перестают верить.
 *
 * Порог (`thresholdAt`) — только ПРЕДВАРИТЕЛЬНЫЙ отбор кандидатов по самому
 * графику (грубая агрегация по бакету, шум округлений и мелких операций).
 * Настоящее подтверждение даёт сверка по журналу (`confirmDivergences`) —
 * поэтому порог здесь может быть щедрым (лучше лишний кандидат уйдёт в
 * `explained` после сверки, чем реальный рассинхрон потеряется на этом
 * этапе).
 */
export function detectDivergences(
  points: TrendPoint[],
  opts?: { bucketMinutes?: number },
): Divergence[] {
  if (opts?.bucketMinutes !== undefined && opts.bucketMinutes >= 240) return [];

  const out: Divergence[] = [];
  const n = points.length;
  if (n < 2) return out;
  const pos = points.map(position);

  const thresholdAt = (idx: number) => Math.max(10000, Math.abs(pos[idx]!) * 0.02);
  const dPosAt = (idx: number) => pos[idx]! - pos[idx - 1]!;
  const chatsDeltaAt = (idx: number) =>
    D(points[idx]!.netUsdt).minus(D(points[idx - 1]!.netUsdt)).toNumber();
  const walletsDeltaAt = (idx: number) =>
    D(points[idx]!.walletsUsdt).minus(D(points[idx - 1]!.walletsUsdt)).toNumber();

  let i = 1;
  while (i < n) {
    const delta = dPosAt(i);
    if (Math.abs(delta) <= thresholdAt(i)) {
      i++;
      continue;
    }

    const chatsDelta = chatsDeltaAt(i);
    const walletsDelta = walletsDeltaAt(i);
    const minLeg = Math.min(Math.abs(chatsDelta), Math.abs(walletsDelta));
    const maxLeg = Math.max(Math.abs(chatsDelta), Math.abs(walletsDelta));
    const oneSided = minLeg <= 0.15 * maxLeg;
    if (!oneSided) {
      // Обе ноги двигались навстречу сопоставимыми суммами — маржа сделки,
      // не рассинхрон. Не кандидат.
      i++;
      continue;
    }

    let cumulative = delta;
    let closedAtOffset: number | null = null;
    let checked = 0;
    for (let k = 1; k <= 3 && i + k < n; k++) {
      cumulative += dPosAt(i + k);
      checked = k;
      if (Math.abs(cumulative) < Math.abs(delta) * 0.1) {
        closedAtOffset = k;
        break;
      }
    }

    let status: Divergence['status'];
    let closedAtTs: string | null = null;
    if (closedAtOffset !== null) {
      status = 'closed';
      closedAtTs = points[i + closedAtOffset]!.ts;
    } else if (checked < 3) {
      status = 'pending';
    } else {
      status = 'open';
    }

    out.push({ index: i, ts: points[i]!.ts, delta, chatsDelta, walletsDelta, status, closedAtTs });

    // При закрытии точки интервала (i+1..i+closedAtOffset) пропускаем —
    // они объяснены этим закрытием, новых кандидатов из них не ищем.
    i = closedAtOffset !== null ? i + closedAtOffset + 1 : i + 1;
  }
  return out;
}

/**
 * Минимальный размер непарной строки, способной подтвердить расхождение:
 * `max(EVIDENCE_FLOOR_USDT, EVIDENCE_SHARE × |delta|)`. Пол в 2 000 USDT
 * совпадает с дефолтным `minUsdt` эндпоинта `/balance-changes/unmatched` —
 * мельче сервер и не отдаёт.
 */
const EVIDENCE_FLOOR_USDT = 2000;
const EVIDENCE_SHARE = 0.3;

/**
 * Окно поиска подтверждающих строк вокруг конкретного кандидата — то же 2 ч,
 * что `PAIR_WINDOW_MS` на бэке (`apps/api/.../balance-changes/matching.ts`).
 * Не импортируется оттуда: web и api собираются раздельно, дублируем
 * осознанно (то же соглашение уже было у прежнего локального `PAD_MS` в
 * `divergenceMatchWindow`, который теперь использует эту же константу).
 */
const EVIDENCE_WINDOW_MS = 2 * 60 * 60 * 1000;

/**
 * Подтверждение расхождений сверкой с журналом: `open` остаётся `open`, если
 * в окне ±`EVIDENCE_WINDOW_MS` вокруг ЭТОГО кандидата (`d.ts`) непарные строки
 * ОДНОГО знака в сумме дают масштаб `|Σ| >= max(2000, 0.3 × |delta|)`.
 * `evidence` — самая крупная отдельная строка той стороны (знака), что дала
 * превышение. Если ни одна из сторон порог не набрала (в т.ч. непарных строк
 * в окне нет вовсе) → скачок объяснён обычными операциями, все ноги нашли
 * пару → `explained`, маркер не рисуется.
 *
 * Окно вокруг кандидата — а не глобально по всему `unmatched` (было ДО этой
 * версии, баг ревью N2): `unmatched` может покрывать несколько часов графика
 * (см. `divergenceMatchWindow` — окно строится по ВСЕМ open-кандидатам
 * разом), и глобальный «самый крупный непарный» одинаково прикреплялся ко
 * ВСЕМ кандидатам сразу, включая те, что были от него в часах — одна крупная
 * непарная строка ложно подтверждала соседние мелкие скачки в чужое время.
 * Здесь для каждого `d` непарные строки сперва фильтруются по близости к
 * `d.ts`, и уже среди них ищутся подходящие/крупнейшие.
 *
 * Сумма ОДНОГО знака — а не одна крупнейшая строка (было ДО этой версии, баг
 * ревью N3): висяк, состоящий из нескольких средних ног одного знака (пример:
 * 5 × 40k = 200k), не давал НИ ОДНОЙ строки крупнее `floor` (60k при delta
 * 200k) — ни одна не подтверждала расхождение, и алерт гас там, где он нужнее
 * всего. Складываем `deltaUsdt` непарных строк ОКНА отдельно по знаку
 * (положительные / отрицательные) и сравниваем `floor` с бОльшим по модулю —
 * так несколько ног одной стороны висяка складываются в подтверждение, а
 * строки ПРОТИВОПОЛОЖНОГО знака (параллельная, не связанная активность) друг
 * друга не гасят и порог не размывают: суммируем каждый знак отдельно, не
 * общий модуль.
 *
 * Прежнее правило (до обеих версий: N2/N3) требовало СОВПАДЕНИЯ одной непарной
 * строки с величиной скачка в пределах 10% и было неверным по построению:
 * `delta` — агрегатный скачок ЗА БАКЕТ, на часовом бакете он складывается из
 * десятков операций. Доля 0.3 — компромисс: одна сторона висяка обычно не
 * мельче трети скачка, но и не требует, чтобы она объясняла его целиком.
 *
 * Исключение: если `unmatched` пришёл с `truncated=true` (сервер обрезал
 * окно по `MAX_ROWS`, см. `matching.ts` — старые строки окна не участвовали
 * в сверке), отсутствие подходящей строки НЕ считается доказательством «всё
 * объяснено» — она могла быть среди отброшенных. В этом случае `open`
 * остаётся `open` с `matchIncomplete: true` вместо понижения до `explained`
 * (цена ложноотрицательного «всё ок» выше цены лишнего алерта — деньги, а не
 * косметика).
 *
 * `closed`/`pending` не трогаются — сверка имеет смысл только для `open`
 * (единственный статус, который вообще может понадобиться понизить/
 * подтвердить).
 */
export function confirmDivergences(
  divs: Divergence[],
  unmatched: UnmatchedRow[],
  truncated = false,
): Divergence[] {
  return divs.map((d) => {
    if (d.status !== 'open') return d;

    const dTs = new Date(d.ts).getTime();
    const nearby = unmatched.filter(
      (u) => Math.abs(new Date(u.occurredAt).getTime() - dTs) <= EVIDENCE_WINDOW_MS,
    );

    // Сумма и крупнейшая строка отдельно по знаку — гасящиеся друг с другом
    // строки разных знаков не должны размывать превышение порога (см. JSDoc).
    let sumPos = 0;
    let sumNeg = 0;
    let largestPos: UnmatchedRow | null = null;
    let largestNeg: UnmatchedRow | null = null;
    for (const u of nearby) {
      const v = Number(u.deltaUsdt);
      if (v > 0) {
        sumPos += v;
        if (!largestPos || v > Number(largestPos.deltaUsdt)) largestPos = u;
      } else if (v < 0) {
        sumNeg += v;
        if (!largestNeg || Math.abs(v) > Math.abs(Number(largestNeg.deltaUsdt))) largestNeg = u;
      }
    }

    const floor = Math.max(EVIDENCE_FLOOR_USDT, EVIDENCE_SHARE * Math.abs(d.delta));
    const exceedsPos = Math.abs(sumPos) >= floor;
    const exceedsNeg = Math.abs(sumNeg) >= floor;
    // Если превысили обе стороны — evidence от той, что дала больший вклад.
    const winner = exceedsNeg && (!exceedsPos || Math.abs(sumNeg) >= Math.abs(sumPos)) ? largestNeg : largestPos;

    if ((exceedsPos || exceedsNeg) && winner) {
      return {
        ...d,
        evidence: {
          title: winner.title,
          ccy: winner.ccy,
          delta: winner.delta,
          deltaUsdt: winner.deltaUsdt,
          actor: winner.actor,
        },
      };
    }
    if (truncated) return { ...d, matchIncomplete: true };
    return { ...d, status: 'explained' as const };
  });
}

/**
 * Окно для запроса `/balance-changes/unmatched`, ПО КАНДИДАТАМ (только
 * `open` — единственный статус, который confirmDivergences вообще
 * пересматривает), а не по всему графику (ревью Task 1: окно всего
 * диапазона на 30д/90д/«Всё» тянет тысячи строк журнала и упирается в
 * предохранитель `MAX_ROWS`, т.е. `truncated=true`, а точность сверки
 * страдает).
 *
 * Кандидатов нет → `null` (вызывающий код должен выключить хук —
 * `enabled: false`, а не звать API с пустым/произвольным окном). Кандидаты
 * есть → `[ts самого раннего − 2ч, ts самого позднего + 2ч]`, зажатое
 * фактическим диапазоном точек графика (сверка не должна просить у сервера
 * данные за пределы того, что вообще показано).
 */
export function divergenceMatchWindow(
  divs: Pick<Divergence, 'ts' | 'status'>[],
  points: Pick<TrendPoint, 'ts'>[],
): { from: string; to: string } | null {
  const candidates = divs.filter((d) => d.status === 'open');
  if (candidates.length === 0 || points.length === 0) return null;

  const candTimes = candidates.map((d) => new Date(d.ts).getTime());
  const pointsMinTs = new Date(points[0]!.ts).getTime();
  const pointsMaxTs = new Date(points[points.length - 1]!.ts).getTime();

  const fromTs = Math.max(Math.min(...candTimes) - EVIDENCE_WINDOW_MS, pointsMinTs);
  const toTs = Math.min(Math.max(...candTimes) + EVIDENCE_WINDOW_MS, pointsMaxTs);
  return { from: new Date(fromTs).toISOString(), to: new Date(toTs).toISOString() };
}

/** Суффикс дельты для тултипа: ' (+123,45)' / ' (−123,45)' / '' если нет или ноль. */
export function fmtDeltaSuffix(curr: number, prev: number | undefined): string {
  if (prev === undefined) return '';
  const d = curr - prev;
  if (d === 0) return '';
  const abs = Math.abs(d).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  return ` (${d > 0 ? '+' : '−'}${abs})`;
}

/**
 * Число со знаком для баннера/пояснений расхождения: всегда 2 знака после
 * запятой (не «до 2», а РОВНО 2 — иначе, например, "123,4" и "123" выглядят
 * непоследовательно в соседних строках тултипа). Ноль — особый случай: «0»,
 * а не «+0» (знак у нулевой величины не несёт смысла).
 */
export function fmtNum(v: number): string {
  if (v === 0) return '0';
  const abs = Math.abs(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v < 0 ? '−' : '+'}${abs}`;
}

/**
 * Преобразует метку бакета истории (`bucketLabel` в
 * `apps/api/src/modules/chat-balances/history.ts`, формат `<число><m|h|d>`:
 * '15m' | '1h' | '4h' | '12h' | '1d') в минуты — для передачи в
 * `detectDivergences({ bucketMinutes })`. Нераспознанный формат → undefined
 * (детектор в этом случае просто не получит ограничение по бакету).
 */
export function bucketLabelToMinutes(bucket: string | undefined): number | undefined {
  if (!bucket) return undefined;
  const m = /^(\d+)([mhd])$/.exec(bucket.trim());
  if (!m) return undefined;
  const value = Number(m[1]);
  const unit = m[2];
  if (unit === 'm') return value;
  if (unit === 'h') return value * 60;
  return value * 60 * 24; // 'd'
}
